import type * as L from 'leaflet';

/**
 * Dynamically loads `leaflet.markercluster`. Its UMD bundle isn't a real ES
 * module — it expects a global `L` to already exist (it does
 * `L.MarkerClusterGroup = ...` rather than importing leaflet itself)
 * instead of receiving one via `require`.
 *
 * Deliberately takes the caller's OWN `leaflet` module namespace as a
 * parameter instead of importing `leaflet` itself. Two earlier versions
 * both imported `leaflet` directly in this file — and confirmed via
 * runtime diagnostics, that caused the bundler to duplicate the `leaflet`
 * module into its own copy inside the async chunk this file's dynamic
 * `import('leaflet.markercluster')` created, SEPARATE from the single
 * shared `leaflet` instance every component (including the one calling
 * this function) actually uses. `window.L = L` and `L.markerClusterGroup`
 * ended up being the SAME object as each other but a DIFFERENT object
 * than the caller's own `import * as L from 'leaflet'` — so the plugin
 * genuinely did attach itself correctly, just to a copy nobody else could
 * see. Taking `L` as a parameter means this file has no `leaflet` import
 * of its own at all, so there's nothing left for the bundler to duplicate
 * — `window.L` is set to the exact same reference the caller will later
 * call `L.markerClusterGroup(...)` on, guaranteed by construction, not by
 * hoping the bundler treats every `import 'leaflet'` as the same module.
 *
 * Call this — `loadMarkerCluster(L)`, passing your own `import * as L from
 * 'leaflet'` — and await it before any `L.markerClusterGroup(...)`.
 * Memoized: safe to call from multiple places/components (though since
 * `window.L` is reassigned each call, all callers must be using the same
 * underlying `leaflet` instance — true for anything that imports the npm
 * package directly rather than pulling in its own separate copy).
 */
let loaded: Promise<void> | null = null;

export function loadMarkerCluster(leaflet: typeof L): Promise<void> {
  if (!loaded) {
    (window as unknown as { L: typeof L }).L = leaflet;
    loaded = import('leaflet.markercluster').then(() => undefined);
  }
  return loaded;
}
